import React, { useState, useEffect, useCallback } from 'react';
import './AIDataPage.css';

import { adminConfig } from '../config/firebase-config.js';

// Use the admin config instead of environment variables
const getApiBaseUrl = () => {
  return adminConfig.apiBaseUrl;
};

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': 'geoff@emertech.nz'
});

// Types
interface AIChunk {
  id: string;
  title: string;
  tier: 1 | 2 | 3 | 4;
  category: string;
  subcategory: string;
  content: string;
  tags: string[];
  relatedChunks: string[];
  businessRelevance: 'high' | 'medium' | 'low';
  status: 'active' | 'draft' | 'archived';
  usageCount: number;
  lastUsed?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  lastEditedBy: string;
  conflicts?: {
    conflictType: 'duplicate' | 'similar' | 'contradictory';
    relatedChunkId: string;
    relatedChunkTitle: string;
    description: string;
    severity: 'low' | 'medium' | 'high';
    resolved: boolean;
    dateDetected: string;
  }[];
}



interface ChunkFilters {
  tier?: 1 | 2 | 3 | 4;
  category?: string;
  subcategory?: string;
  status?: 'active' | 'draft' | 'archived';
  tags?: string[];
  search?: string;
  sortBy?: 'title' | 'tier' | 'category' | 'usageCount' | 'lastUsed' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
  hasConflicts?: boolean;
  conflictType?: 'duplicate' | 'similar' | 'contradictory';
  conflictSeverity?: 'low' | 'medium' | 'high';
  conflictResolved?: boolean;
}

// Removed unused UsageStats interface

interface DashboardSummary {
  totalChunks: number;
  activeChunks: number;
  unusedChunks: number;
  totalQueries: number;
  averageSimilarity: number;
  topCategories: { category: string; usage: number }[];
  contentGaps: number;
}

type TabType = 'dashboard' | 'browse' | 'editor' | 'analytics';

const AIDataPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [chunks, setChunks] = useState<AIChunk[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [filters, setFilters] = useState<ChunkFilters>({});
  const [selectedChunk, setSelectedChunk] = useState<AIChunk | null>(null);
  const [editingChunk, setEditingChunk] = useState<AIChunk | null>(null);
  const [dashboardSummary, setDashboardSummary] = useState<DashboardSummary | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [chunksLoading, setChunksLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflictsCount, setConflictsCount] = useState(0);
  const [showTagsFilter, setShowTagsFilter] = useState(false);

  // Handle URL parameters for conflicts filter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasConflicts = urlParams.get('hasConflicts');
    
    if (hasConflicts === 'true') {
      setFilters(prev => ({ ...prev, hasConflicts: true }));
      setActiveTab('browse');
    }
  }, []);

  // Calculate conflicts when chunks change
  useEffect(() => {
    let totalConflicts = 0;
    chunks.forEach(chunk => {
      if (chunk.conflicts && chunk.conflicts.length > 0) {
        totalConflicts += chunk.conflicts.filter(c => !c.resolved).length;
      }
    });
    setConflictsCount(totalConflicts);
  }, [chunks]);

  const loadTags = async () => {
    try {
      // Load tags from actual chunk data
      const response = await fetch(`${getApiBaseUrl()}/getAIChunks`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (response.ok) {
        // Extract unique tags from all chunks
        const allTags = data.chunks.flatMap((chunk: any) => chunk.tags || []);
        const uniqueTags = [...new Set(allTags)]
          .filter(tag => tag) // Remove undefined/null tags
          .sort() as string[];
        setAvailableTags(uniqueTags);
        setError(null); // Clear any previous errors
      } else {
        setError('Failed to load tags');
      }
    } catch (err) {
      setError('Failed to load tags');
    }
  };

  const loadChunks = useCallback(async () => {
    setChunksLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          if (key === 'tags' && Array.isArray(value)) {
            // Handle tags array - add each tag as a separate parameter
            value.forEach(tag => params.append('tags', tag));
          } else {
            params.append(key, value.toString());
          }
        }
      });

      const response = await fetch(`${getApiBaseUrl()}/getAIChunks?${params}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (response.ok) {
        setChunks(data.chunks);
        setError(null); // Clear any previous errors
      } else {
        setError('Failed to load chunks');
      }
    } catch (err) {
      setError('Failed to load chunks');
    } finally {
      setChunksLoading(false);
    }
  }, [filters]);

  // Load initial data
  useEffect(() => {
    loadTags();
    loadDashboardSummary();
    // Load chunks for dashboard to calculate conflicts
    loadChunksForDashboard();
  }, []);

  // Load chunks when filters change
  useEffect(() => {
    if (activeTab === 'browse') {
      loadChunks();
    }
  }, [filters, activeTab, loadChunks]);

  const loadChunksForDashboard = async () => {
    setChunksLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/getAIChunks`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (response.ok) {
        setChunks(data.chunks);
        setError(null);
      } else {
        setError('Failed to load chunks for dashboard');
      }
    } catch (err) {
      setError('Failed to load chunks for dashboard');
    } finally {
      setChunksLoading(false);
    }
  };

  const loadDashboardSummary = async () => {
    setDashboardLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/getAnalyticsDashboard`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (response.ok) {
        setDashboardSummary(data.summary);
        setError(null); // Clear any previous errors
      } else {
        setError('Failed to load dashboard summary');
      }
    } catch (err) {
      setError('Failed to load dashboard summary');
    } finally {
      setDashboardLoading(false);
    }
  };

  const saveChunk = async () => {
    if (!editingChunk) return;
    
    setSaving(true);
    try {
      const chunkData = {
        title: editingChunk.title,
        content: editingChunk.content,
        category: editingChunk.category,
        subcategory: editingChunk.subcategory,
        tier: editingChunk.tier,
        tags: editingChunk.tags,
        businessRelevance: editingChunk.businessRelevance,
        status: editingChunk.status
      };

      let response;
      if (editingChunk.id === 'new') {
        // Create new chunk
        response = await fetch(`${getApiBaseUrl()}/createAIChunk`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(chunkData)
        });
      } else {
        // Update existing chunk
        response = await fetch(`${getApiBaseUrl()}/updateAIChunk`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ id: editingChunk.id, ...chunkData })
        });
      }

      if (response.ok) {
        const data = await response.json();
        setSelectedChunk(data.chunk);
        setEditingChunk(null);
        loadChunks(); // Refresh the chunks list
        setError(null);
      } else {
        setError('Failed to save chunk');
      }
    } catch (err) {
      setError('Failed to save chunk');
    } finally {
      setSaving(false);
    }
  };

  const deleteChunk = async () => {
    if (!selectedChunk || selectedChunk.id === 'new') return;
    
    if (!window.confirm(`Are you sure you want to delete "${selectedChunk.title}"? This action cannot be undone.`)) {
      return;
    }
    
    setDeleting(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/deleteAIChunk?id=${selectedChunk.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setSelectedChunk(null);
        setEditingChunk(null);
        loadChunks(); // Refresh the chunks list
        setError(null);
        setActiveTab('browse'); // Go back to browse tab
      } else {
        setError('Failed to delete chunk');
      }
    } catch (err) {
      setError('Failed to delete chunk');
    } finally {
      setDeleting(false);
    }
  };

  const getTierBadge = (tier: number) => {
    const tierConfig = {
      1: { label: 'Critical', color: 'red' },
      2: { label: 'Core', color: 'yellow' },
      3: { label: 'Supporting', color: 'green' },
      4: { label: 'Marketing', color: 'blue' }
    };
    const config = tierConfig[tier as keyof typeof tierConfig];
    return (
      <span className={`tier-badge tier-${config.color}`}>
        Tier {tier} - {config.label}
      </span>
    );
  };

  const getStatusBadge = (status: string | undefined) => {
    const statusConfig = {
      active: { label: 'Active', color: 'green' },
      draft: { label: 'Draft', color: 'yellow' },
      archived: { label: 'Archived', color: 'gray' }
    };

    // Handle undefined/null status
    if (!status) {
      return (
        <span className="status-badge status-gray">
          Unknown
        </span>
      );
    }

    const config = statusConfig[status as keyof typeof statusConfig] || { label: status || 'Unknown', color: 'gray' };
    return (
      <span className={`status-badge status-${config.color}`}>
        {config.label}
      </span>
    );
  };

  const renderDashboard = () => (
    <div className="dashboard-content">
      <h2>AI Data Dashboard</h2>
      {dashboardLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      ) : dashboardSummary ? (
        <div className="dashboard-stats">
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Chunks</h3>
              <div className="stat-value">{dashboardSummary.totalChunks}</div>
            </div>
            <div className="stat-card">
              <h3>Active Chunks</h3>
              <div className="stat-value">{dashboardSummary.activeChunks}</div>
            </div>
            <div className="stat-card">
              <h3>Unused Chunks</h3>
              <div className="stat-value">{dashboardSummary.unusedChunks}</div>
            </div>
            <div className="stat-card">
              <h3>Total Queries</h3>
              <div className="stat-value">{dashboardSummary.totalQueries}</div>
            </div>
            <div className="stat-card">
              <h3>Avg Similarity</h3>
              <div className="stat-value">{(dashboardSummary.averageSimilarity * 100).toFixed(1)}%</div>
            </div>
            <div className="stat-card" onClick={() => {
              setFilters({ hasConflicts: true });
              setActiveTab('browse');
            }} style={{ cursor: 'pointer' }}>
              <h3>Conflicts</h3>
              <div className="stat-value">{chunksLoading ? '...' : conflictsCount}</div>
            </div>
          </div>
          
          <div className="top-categories">
            <h3>Top Categories by Usage</h3>
            <div className="category-list">
              {dashboardSummary.topCategories.map(cat => (
                <div key={cat.category} className="category-item">
                  <span className="category-name">{cat.category}</span>
                  <span className="category-usage">{cat.usage} uses</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="no-data">
          <p>No dashboard data available</p>
          <button onClick={loadDashboardSummary}>Reload Dashboard</button>
        </div>
      )}
    </div>
  );

  const renderBrowser = () => (
    <div className="browser-content">
      <h2>Browse AI Chunks</h2>
      
      {/* Filters */}
      <div className="filters-section">
        <div className="filter-row">
          <select 
            value={filters.tier || ''} 
            onChange={(e) => setFilters({...filters, tier: e.target.value ? parseInt(e.target.value) as 1|2|3|4 : undefined})}
          >
            <option value="">All Tiers</option>
            <option value="1">Tier 1 - Critical</option>
            <option value="2">Tier 2 - Core</option>
            <option value="3">Tier 3 - Supporting</option>
            <option value="4">Tier 4 - Marketing</option>
          </select>

          <select 
            value={filters.status || ''} 
            onChange={(e) => setFilters({...filters, status: e.target.value as 'active'|'draft'|'archived' || undefined})}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>

          <input 
            type="text" 
            placeholder="Search chunks..." 
            value={filters.search || ''} 
            onChange={(e) => setFilters({...filters, search: e.target.value || undefined})}
          />
        </div>
        
        {/* Tags Filter */}
        <div className="tags-filter-section">
          <div className="tags-filter-header">
            <button 
              className="tags-filter-toggle"
              onClick={() => setShowTagsFilter(!showTagsFilter)}
            >
              Filter by Tags {showTagsFilter ? '▼' : '▶'}
            </button>
            {showTagsFilter && (
              <button 
                className="hide-tags-btn"
                onClick={() => setShowTagsFilter(false)}
              >
                Hide Tags
              </button>
            )}
          </div>
          
          {showTagsFilter && (
            <>
              <div className="tags-grid">
                {availableTags.map(tag => (
                  <label key={tag} className="tag-checkbox">
                    <input
                      type="checkbox"
                      checked={filters.tags?.includes(tag) || false}
                      onChange={(e) => {
                        const currentTags = filters.tags || [];
                        if (e.target.checked) {
                          setFilters({...filters, tags: [...currentTags, tag]});
                        } else {
                          setFilters({...filters, tags: currentTags.filter(t => t !== tag)});
                        }
                      }}
                    />
                    <span className="tag-label">{tag}</span>
                    <span className="tag-count">
                      ({chunks.filter(chunk => chunk.tags?.includes(tag)).length})
                    </span>
                  </label>
                ))}
              </div>
              {filters.tags && filters.tags.length > 0 && (
                <div className="selected-tags">
                  <strong>Selected tags:</strong>
                  {filters.tags.map(tag => (
                    <span key={tag} className="selected-tag">
                      {tag}
                      <button 
                        onClick={() => {
                          const newTags = filters.tags?.filter(t => t !== tag);
                          setFilters({...filters, tags: newTags?.length ? newTags : undefined});
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <button 
                    className="clear-tags"
                    onClick={() => setFilters({...filters, tags: undefined})}
                  >
                    Clear all
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Chunks List */}
      <div className="chunks-list">
        {chunksLoading ? (
          <div className="loading">Loading chunks...</div>
        ) : (
          chunks.map(chunk => (
            <div key={chunk.id} className="chunk-item" onClick={() => {
              setSelectedChunk(chunk);
              setEditingChunk({...chunk});
              setActiveTab('editor');
            }}>
              <div className="chunk-header">
                <div className="chunk-title">{chunk.title}</div>
                <div className="chunk-badges">
                  {getTierBadge(chunk.tier)}
                  {getStatusBadge(chunk.status)}
                  {chunk.conflicts && chunk.conflicts.length > 0 && (
                    <span className={`conflict-badge ${chunk.conflicts.some(c => !c.resolved) ? 'unresolved' : 'resolved'}`}>
                      {chunk.conflicts.filter(c => !c.resolved).length > 0 
                        ? `${chunk.conflicts.filter(c => !c.resolved).length} Conflicts` 
                        : 'Conflicts Resolved'}
                    </span>
                  )}
                </div>
              </div>
              <div className="chunk-meta">
                <span className="category">{chunk.category}</span>
                {chunk.subcategory && <span className="subcategory">/ {chunk.subcategory}</span>}
                <span className="usage-count">{chunk.usageCount} uses</span>
                {chunk.lastUsed && <span className="last-used">Last used: {new Date(chunk.lastUsed).toLocaleDateString()}</span>}
              </div>
              {chunk.conflicts && chunk.conflicts.length > 0 && (
                <div className="chunk-conflicts">
                  {chunk.conflicts.slice(0, 2).map((conflict, index) => (
                    <div key={index} className={`conflict-item ${conflict.resolved ? 'resolved' : 'unresolved'}`}>
                      <span className="conflict-type">{conflict.conflictType}</span>
                      <span className="conflict-description">{conflict.description}</span>
                      <span className={`conflict-severity ${conflict.severity}`}>{conflict.severity}</span>
                    </div>
                  ))}
                  {chunk.conflicts.length > 2 && (
                    <div className="more-conflicts">+{chunk.conflicts.length - 2} more conflicts</div>
                  )}
                </div>
              )}
              <div className="chunk-preview">
                {chunk.content.substring(0, 150)}...
              </div>
              <div className="chunk-keywords">
                {(chunk.tags || []).slice(0, 5).map(tag => (
                  <span key={tag} className="keyword-tag">{tag}</span>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderEditor = () => (
    <div className="editor-content">
      <div className="editor-header-section">
        <h2>Chunk Editor</h2>
        <button 
          className="create-chunk-btn"
          onClick={() => {
            const newChunk = {
              id: 'new',
              title: '',
              content: '',
              category: '',
              subcategory: '',
              tier: 1 as const,
              tags: [],
              relatedChunks: [],
              businessRelevance: 'medium' as const,
              status: 'draft' as const,
              usageCount: 0,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              createdBy: 'admin',
              lastEditedBy: 'admin'
            };
            setSelectedChunk(newChunk);
            setEditingChunk(newChunk);
          }}
        >
          + Create New Chunk
        </button>
      </div>
      {selectedChunk ? (
        <div className="chunk-editor">
          <div className="editor-header">
            <h3>{selectedChunk.id === 'new' ? 'Creating New Chunk' : `Editing: ${selectedChunk.title}`}</h3>
            <div className="editor-header-buttons">
              {selectedChunk.id !== 'new' && (
                <button 
                  className="delete-btn"
                  onClick={deleteChunk}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              )}
              <button onClick={() => setSelectedChunk(null)}>Close</button>
            </div>
          </div>
          <div className="editor-form">
            <div className="form-group">
              <label>Title</label>
              <input 
                type="text" 
                value={editingChunk?.title || selectedChunk.title} 
                onChange={(e) => editingChunk && setEditingChunk({...editingChunk, title: e.target.value})}
                readOnly={!editingChunk}
              />
            </div>
            <div className="form-group">
              <label>Content</label>
              <textarea 
                value={editingChunk?.content || selectedChunk.content} 
                rows={10} 
                onChange={(e) => editingChunk && setEditingChunk({...editingChunk, content: e.target.value})}
                readOnly={!editingChunk}
              />
            </div>
            <div className="form-group">
              <label>Category</label>
              <input 
                type="text" 
                value={editingChunk?.category || selectedChunk.category} 
                onChange={(e) => editingChunk && setEditingChunk({...editingChunk, category: e.target.value})}
                readOnly={!editingChunk}
                placeholder="Enter category"
              />
            </div>
            <div className="form-group">
              <label>Subcategory</label>
              <input 
                type="text" 
                value={editingChunk?.subcategory || selectedChunk.subcategory} 
                onChange={(e) => editingChunk && setEditingChunk({...editingChunk, subcategory: e.target.value})}
                readOnly={!editingChunk}
              />
            </div>
            <div className="form-group">
              <label>Tier</label>
              <select 
                value={editingChunk?.tier || selectedChunk.tier}
                onChange={(e) => editingChunk && setEditingChunk({...editingChunk, tier: parseInt(e.target.value) as 1|2|3|4})}
                disabled={!editingChunk}
              >
                <option value="1">Tier 1 - Critical</option>
                <option value="2">Tier 2 - Core</option>
                <option value="3">Tier 3 - Supporting</option>
                <option value="4">Tier 4 - Marketing</option>
              </select>
            </div>
            <div className="form-group">
              <label>Tags (comma-separated)</label>
              <input
                type="text"
                value={editingChunk?.tags?.join(', ') || selectedChunk.tags?.join(', ') || ''}
                onChange={(e) => editingChunk && setEditingChunk({...editingChunk, tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)})}
                readOnly={!editingChunk}
              />
            </div>
            <div className="form-group">
              <label>Business Relevance</label>
              <select 
                value={editingChunk?.businessRelevance || selectedChunk.businessRelevance}
                onChange={(e) => editingChunk && setEditingChunk({...editingChunk, businessRelevance: e.target.value as 'high'|'medium'|'low'})}
                disabled={!editingChunk}
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select 
                value={editingChunk?.status || selectedChunk.status}
                onChange={(e) => editingChunk && setEditingChunk({...editingChunk, status: e.target.value as 'active'|'draft'|'archived'})}
                disabled={!editingChunk}
              >
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            {!editingChunk && (
              <div className="form-group">
                <label>Usage Analytics</label>
                <div className="usage-stats">
                  <div>Usage Count: {selectedChunk.usageCount}</div>
                  {selectedChunk.lastUsed && (
                    <div>Last Used: {new Date(selectedChunk.lastUsed).toLocaleString()}</div>
                  )}
                </div>
              </div>
            )}
            <div className="form-actions">
              {editingChunk ? (
                <>
                  <button onClick={saveChunk} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={() => setEditingChunk(null)}>Cancel</button>
                </>
              ) : (
                <button onClick={() => setEditingChunk({...selectedChunk})}>Edit</button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="no-selection">
          <p>Select a chunk from the browser to edit it.</p>
          <button onClick={() => setActiveTab('browse')}>Browse Chunks</button>
        </div>
      )}
    </div>
  );

  const renderAnalytics = () => (
    <div className="analytics-content">
      <h2>Analytics & Insights</h2>
      <div className="analytics-placeholder">
        <p>Detailed analytics coming soon...</p>
        <ul>
          <li>Chunk usage trends</li>
          <li>Popular queries analysis</li>
          <li>Content gap identification</li>
          <li>Performance metrics</li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="ai-data-page">
      <div className="page-header">
        <h1>AI Data Management</h1>
        <p>Manage and optimize your AI chatbot's knowledge base</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="tabs-container">
        <div className="tabs">
          <button 
            className={activeTab === 'dashboard' ? 'active' : ''} 
            onClick={() => setActiveTab('dashboard')}
          >
            📊 Dashboard
          </button>
          <button 
            className={activeTab === 'browse' ? 'active' : ''} 
            onClick={() => setActiveTab('browse')}
          >
            🔍 Browse Chunks
          </button>
          <button 
            className={activeTab === 'editor' ? 'active' : ''} 
            onClick={() => setActiveTab('editor')}
          >
            ✏️ Editor
          </button>
          <button 
            className={activeTab === 'analytics' ? 'active' : ''} 
            onClick={() => setActiveTab('analytics')}
          >
            📈 Analytics
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'browse' && renderBrowser()}
          {activeTab === 'editor' && renderEditor()}
          {activeTab === 'analytics' && renderAnalytics()}
        </div>
      </div>
    </div>
  );
};

export default AIDataPage; 